import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { 
  getContinueListening, 
  addContinueListening, 
  deleteContinueListening 
} from '../api/continueListeningAPI';

// Hook to fetch continue listening items
export const useContinueListening = () => {
  const { setError } = useAuthStore();
  
  const {
    data: continueListeningData,
    isLoading,
    error,
    refetch
  } = useQuery('continueListening', getContinueListening, {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch continue listening items');
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    continueListeningData,
    isLoading,
    error,
    refetchContinueListening: refetch
  };
};

// Hook to add a continue listening item
export const useAddContinueListening = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const validateItem = (item_type, item_id) => {
    const validItemTypes = ['ebook', 'short', 'video', 'audio'];
    
    if (!validItemTypes.includes(item_type)) {
      throw new Error("Invalid item type. Must be one of: ebook, short, video, audio");
    }
    
    if (!item_id) {
      throw new Error("Item ID is required");
    }
  };

  const addContinueListeningMutation = useMutation(addContinueListening, {
    onMutate: (variables) => {
      try {
        validateItem(variables.item_type, variables.item_id);
      } catch (error) {
        toast.error(error.message);
        return Promise.reject(error);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries('continueListening');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to add item to continue listening';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    addContinueListening: addContinueListeningMutation.mutate,
    isLoading: addContinueListeningMutation.isLoading,
    isSuccess: addContinueListeningMutation.isSuccess,
    isError: addContinueListeningMutation.isError,
    error: addContinueListeningMutation.error,
    reset: addContinueListeningMutation.reset
  };
};

// Hook to delete a continue listening item
export const useDeleteContinueListening = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const deleteContinueListeningMutation = useMutation(deleteContinueListening, {
    onSuccess: () => {
      toast.success("Item removed from continue listening", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('continueListening');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to remove item from continue listening';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    deleteContinueListening: deleteContinueListeningMutation.mutate,
    isLoading: deleteContinueListeningMutation.isLoading,
    isSuccess: deleteContinueListeningMutation.isSuccess,
    isError: deleteContinueListeningMutation.isError,
    error: deleteContinueListeningMutation.error,
    reset: deleteContinueListeningMutation.reset
  };
};